import type { Hole, HoleSet } from "./types.js";

/** Course handicap from index + slope, standard formula. Falls back to the raw
 * index when slope is unknown (manual course entry may omit it). */
export function courseHandicap(handicapIndex: number, slope: number | null): number {
  if (slope == null) return Math.round(handicapIndex);
  return Math.round(handicapIndex * (slope / 113));
}

/**
 * Per-round handicap allowance, halved for a nine-hole round per spec §6.
 *
 * Keyed on how many holes are actually being played rather than on the round's
 * `holeSet`, because those stopped being the same question the moment nine-hole
 * *courses* existed: a round at Grassmere has `holeSet: "all18"` — it plays the whole
 * card — and is still nine holes, so it still gets half the strokes. Pass
 * `holesInPlay(...).length`.
 */
export function effectiveAllowancePercent(basePercent: number, holesPlayed: number): number {
  return holesPlayed > HALF_ROUND ? basePercent : basePercent / 2;
}

/** Holes in a nine. The whole of a nine-hole card, or half of an eighteen. */
export const HALF_ROUND = 9;

/** Holes in a full round on an eighteen-hole course. */
export const FULL_ROUND = 18;

/**
 * The hole counts a card may have.
 *
 * Nine and eighteen, and nothing between: a scorecard is one or the other, every
 * stroke-index and handicap rule in the game assumes it, and a card of eleven holes is
 * a data-entry mistake rather than a course. Twenty-seven-hole clubs are three nines
 * with their own names and get entered as separate courses, which is how they are
 * played and how golfcourseapi.com stores them.
 */
export const CARD_HOLE_COUNTS: readonly number[] = [HALF_ROUND, FULL_ROUND];

export function playingHandicap(ch: number, allowancePercent: number): number {
  return Math.round(ch * (allowancePercent / 100));
}

/**
 * Which holes of a card a round actually plays.
 *
 * The single source of truth for that question — every count, every total and every
 * "N of 18" in the app derives from this rather than from `holeSet` alone, because
 * `holeSet` names a *choice* and only the card knows how many holes there are to
 * choose from. `"all18"` is a stored value from when every card had eighteen holes;
 * read it as "all of them".
 *
 * A nine-hole card has no back nine to ask for. Setup won't offer one, but a round
 * saved before its course was re-entered as nine holes still can, and a round that
 * silently scores zero holes is far worse than one that plays the nine it has — so
 * `back9` on a short card falls back to the whole card.
 */
export function holesInPlay(holes: Hole[], holeSet: HoleSet): Hole[] {
  if (holeSet === "all18") return holes;
  if (holeSet === "front9") return holes.filter((h) => h.number <= HALF_ROUND);
  const back = holes.filter((h) => h.number > HALF_ROUND);
  return back.length > 0 ? back : holes;
}

/**
 * How many holes a round plays.
 *
 * `holes` is the tee set's own card. When it is missing — a round whose course has
 * been deleted, or a rules preview built before one was picked — this falls back to
 * the old assumption, which is the only sensible guess with nothing to count.
 */
export function holesPlayedCount(holes: Hole[] | undefined, holeSet: HoleSet): number {
  if (holes && holes.length > 0) return holesInPlay(holes, holeSet).length;
  return holeSet === "all18" ? FULL_ROUND : HALF_ROUND;
}

/**
 * Allocates strokes across the holes actually in play. "Stroke index <= handicap,
 * wrapping above 18" is really "rank the holes in play by difficulty and give the
 * hardest (handicap mod n) of them an extra stroke, n times over" — n is 18 for a
 * full round, and 9 for a nine, whether that nine is half of an eighteen-hole card
 * (using that half's own stroke indexes, not a hardcoded odd/even split) or the whole
 * of a nine-hole course. It reads `holes.length`, so it has always been right about
 * this and needs nothing said about hole counts.
 */
export function allocateStrokes(
  playingHcp: number,
  holes: Pick<Hole, "number" | "strokeIndex">[],
): Map<number, number> {
  const n = holes.length;
  const strokes = new Map<number, number>();
  if (n === 0) return strokes;

  const base = Math.floor(playingHcp / n);
  const remainder = playingHcp - base * n;

  const byDifficulty = [...holes].sort((a, b) => a.strokeIndex - b.strokeIndex);

  for (const hole of holes) strokes.set(hole.number, base);
  for (let i = 0; i < remainder; i++) {
    const hole = byDifficulty[i];
    if (!hole) break;
    strokes.set(hole.number, (strokes.get(hole.number) ?? base) + 1);
  }

  return strokes;
}

export function netStrokes(gross: number, strokesReceived: number): number {
  return gross - strokesReceived;
}
